import React, { ChangeEvent, FC, useCallback, useState } from 'react';
import { Link, useHistory, useRouteMatch } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormGroup,
  FormText,
  Input,
  Label,
  Row,
} from 'reactstrap';
import {
  ExtendedHotelReleaseGroup,
  getReleasePeriod,
  HotelIntegrationProviders,
  HotelReleaseGroupCreateAttributes,
  HotelReleaseGroupUpdateAttributes,
  ReleaseForceEntry,
  ReleaseGroupStatus,
  ReleaseGroupType,
  ReleaseGroupVisibility,
  ReleaseRequireRegistration,
} from '../../../../../shared/hotel';
import { TaskStatus } from '../../../../../shared/task';
import { ActionButtonModal, DateTime, LinkButton, MaterialIcon } from '../../../../components';
import { ProductSelection } from '../../../../components/ProductSelection';
import { RoleSelection } from '../../../../components/RoleSelection';
import {
  NullableTimeZoneInputField,
  TimeZoneInputField,
} from '../../../../components/TimeZoneInputField';
import { useTranslation } from '../../../../translations';
import {
  Fetcher,
  useConvention,
  useFetcher,
  useForm,
  useInputCheckboxModel,
  useInputModel,
  useObject,
} from '../../../../utils';

export const ReleaseGroupList: FC = () => {
  const match = useRouteMatch();
  const { ts } = useTranslation();

  const data = useFetcher(async () => {
    return await api.getAllHotelReleases();
  });

  if (!data.success) {
    return <Fetcher result={data} />;
  }

  return (
    <div>
      <LinkButton color="primary" id="createReleaseGroup" to={`${match.url}/new`}>
        {ts('create_release_group')}
      </LinkButton>
      <div>
        <Row className="justify-content-center">
          {data.data.map((release) => (
            <Col className="mb-10" key={release.id} lg={8} xs={12}>
              <ReleaseGroupListElement release={release} />
            </Col>
          ))}
        </Row>
      </div>
    </div>
  );
};

const ReleaseGroupListElement: FC<{
  readonly release: ExtendedHotelReleaseGroup;
}> = ({ release }) => {
  const { ts } = useTranslation();
  const key = `release${release.id}`;

  // todo: do we implement deleting release groups?
  const deleteOption = useCallback(() => {
    // await api.deleteOption(option.source, option.id);
    toast.success(ts('option_has_been_deleted'));
  }, [ts]);

  return (
    <Card id={key}>
      <CardBody>
        <div
          style={{
            minWidth: '200px',
            position: 'absolute',
            right: '15px',
            textAlign: 'right',
            top: '15px',
          }}
        />
        <h4 style={{ marginRight: '30px' }}>
          {release.name} <small>({release.displayName})</small>
        </h4>
        <div className="float-end">
          <LinkButton
            color="primary"
            id={`${key}view`}
            to={`/housekeeping/hotel/groups/${release.id}/view`}
          >
            {ts('view_edit_details')}
          </LinkButton>{' '}
          <LinkButton
            color="primary"
            id={`${key}Edit`}
            to={`/housekeeping/hotel/groups/${release.id}/edit`}
          >
            <MaterialIcon name="edit" />
          </LinkButton>{' '}
          <ActionButtonModal
            actionContent="common.delete"
            buttonContent={<MaterialIcon name="delete" />}
            color="danger"
            id={`${key}Delete`}
            onComplete={deleteOption}
            title="delete_option"
          >
            {ts('are_you_sure_you_want_to_delete')}
          </ActionButtonModal>
        </div>
        <span>{release.description}</span>
        <ReleaseGroupPills group={release} />
      </CardBody>
    </Card>
  );
};

const ReleaseGroupPills: FC<{ readonly group: ExtendedHotelReleaseGroup }> = ({ group }) => {
  const { sendEmailsTask, createLinksTask } = group;

  return (
    <div className="badge-list compact">
      <Badge color="success">{getReleasePeriod(group, new Date())}</Badge>
      {createLinksTask && (
        <Link to={`/housekeeping/auditlog/Task/${createLinksTask.id}`}>
          <Badge color={createLinksTask.status === TaskStatus.Completed ? 'primary' : 'warning'}>
            Create links task scheduled for: <DateTime value={createLinksTask.runAt} /> (
            {createLinksTask.status})
          </Badge>
        </Link>
      )}
      {sendEmailsTask && (
        <Link to={`/housekeeping/auditlog/Task/${sendEmailsTask.id}`}>
          <Badge color={sendEmailsTask.status === TaskStatus.Completed ? 'primary' : 'warning'}>
            Send emails task scheduled for: <DateTime value={sendEmailsTask.runAt} /> (
            {sendEmailsTask.status})
          </Badge>
        </Link>
      )}
    </div>
  );
};

export const CreateReleaseGroupForm: FC = () => {
  return <ReleaseGroupForm />;
};

export const EditReleaseGroupForm: FC = () => {
  const match = useRouteMatch<{ id: string }>();

  const group = useFetcher(async () => {
    return await api.getHotelReleaseById(match.params.id);
  });

  if (!group.success) {
    return <Fetcher result={group} />;
  }

  return <ReleaseGroupForm inputGroup={group.data} />;
};

// eslint-disable-next-line max-lines-per-function
const ReleaseGroupForm: FC<{ readonly inputGroup?: ExtendedHotelReleaseGroup }> = ({
  inputGroup,
}) => {
  const history = useHistory();
  const { timeZone } = useConvention();
  const { ts } = useTranslation();

  const [releaseGroup, setReleaseGroup] = useState<HotelReleaseGroupCreateAttributes>(() => ({
    name: inputGroup?.name ?? '',
    displayName: inputGroup?.displayName ?? '',
    description: inputGroup?.description ?? '',
    entryStartAt: inputGroup?.entryStartAt ? new Date(inputGroup.entryStartAt) : new Date(),
    entryEndAt: inputGroup?.entryEndAt ? new Date(inputGroup.entryEndAt) : new Date(),
    type: inputGroup?.type ?? ReleaseGroupType.RandomDraw,
    status: inputGroup?.status ?? ReleaseGroupStatus.Draft,
    releaseStartAt: inputGroup?.releaseStartAt ? new Date(inputGroup.releaseStartAt) : new Date(),
    releaseEndAt: inputGroup?.releaseEndAt ? new Date(inputGroup.releaseEndAt) : new Date(),
    validityPeriod: inputGroup?.validityPeriod ?? 0,
    requireRegistration: inputGroup?.requireRegistration ?? ReleaseRequireRegistration.Required,
    requireNotWon: false,
    showRoomAvailability: inputGroup?.showRoomAvailability ?? false,
    visibility: inputGroup?.visibility ?? ReleaseGroupVisibility.Hidden,
    integrationId: inputGroup?.integrationId ?? '',
    requiredRoleIds: inputGroup?.requiredRoles.map((r) => r.id) ?? [],
    requiredProductIds: inputGroup?.requiredProducts.map((r) => r.id) ?? [],
    meta: inputGroup?.meta ?? {},
    forceEntry: inputGroup?.forceEntry ?? ReleaseForceEntry.None,
  }));

  const setName = useInputModel(setReleaseGroup, 'name');
  const setDisplayName = useInputModel(setReleaseGroup, 'displayName');
  const setDescription = useInputModel(setReleaseGroup, 'description');
  const [, setEntryStartAt] = useObject(releaseGroup, setReleaseGroup, 'entryStartAt');
  const [, setEntryEndAt] = useObject(releaseGroup, setReleaseGroup, 'entryEndAt');
  const setType = useInputModel(setReleaseGroup, 'type');
  const setStatus = useInputModel(setReleaseGroup, 'status');
  const [, setReleaseStartAt] = useObject(releaseGroup, setReleaseGroup, 'releaseStartAt');
  const [, setReleaseEndAt] = useObject(releaseGroup, setReleaseGroup, 'releaseEndAt');
  const setValidityPeriod = useInputModel(setReleaseGroup, 'validityPeriod');
  const setIntegrationId = useInputModel(setReleaseGroup, 'integrationId');
  const setRequireRegistration = useInputModel(setReleaseGroup, 'requireRegistration');
  const setForceEntry = useInputModel(setReleaseGroup, 'forceEntry');
  const setRequireNotWon = useInputCheckboxModel(setReleaseGroup, 'requireNotWon');
  const setShowRoomAvailability = useInputCheckboxModel(setReleaseGroup, 'showRoomAvailability');
  const [, setProductIds] = useObject(releaseGroup, setReleaseGroup, 'requiredProductIds');
  const [, setRoleIds] = useObject(releaseGroup, setReleaseGroup, 'requiredRoleIds');
  const [, setVisibility] = useObject(releaseGroup, setReleaseGroup, 'visibility');

  const releaseGroupStatus = [
    { key: ReleaseGroupStatus.Draft, name: 'Draft' },
    { key: ReleaseGroupStatus.Ready, name: 'Ready' },
  ];

  const releaseGroupTypes = [
    { key: ReleaseGroupType.RandomDraw, name: 'Random Draw' },
    { key: ReleaseGroupType.FirstComeFirstServe, name: 'First Come First Serve' },
  ];

  const releaseGroupVisibilities = [
    { key: ReleaseGroupVisibility.Hidden, name: 'Hidden' },
    { key: ReleaseGroupVisibility.AlwaysVisible, name: 'Always Visible' },
    { key: ReleaseGroupVisibility.EligibleOnly, name: 'Eligible Only' },
  ];

  const requireRegistrationValues = [
    { key: ReleaseRequireRegistration.Optional, name: 'Hidden' },
    { key: ReleaseRequireRegistration.Required, name: 'Required' },
    { key: ReleaseRequireRegistration.RequirePaid, name: 'Required Paid' },
  ];

  const releaseForceEntryValues = [
    { key: ReleaseForceEntry.None, name: 'None' },
    { key: ReleaseForceEntry.ForceByProductIds, name: 'By Product' },
    { key: ReleaseForceEntry.ForceByRoleIds, name: 'By Role' },
    { key: ReleaseForceEntry.ForceByRoleAndProduct, name: 'By Role and Product' },
    { key: ReleaseForceEntry.ForceByRoleOrProduct, name: 'By Role or Product' },
  ];

  const integrations = useFetcher(async () => {
    return await api.getHotelIntegrations();
  }, []);

  const integration = integrations.data?.find((i) => i.id === releaseGroup.integrationId);

  const form = useForm(async () => {
    if (inputGroup) {
      const payload: HotelReleaseGroupUpdateAttributes = {
        name: releaseGroup.name,
        displayName: releaseGroup.displayName,
        description: releaseGroup.description,
        entryStartAt: releaseGroup.entryStartAt,
        entryEndAt: releaseGroup.entryEndAt,
        releaseStartAt: releaseGroup.releaseStartAt,
        releaseEndAt: releaseGroup.releaseEndAt,
        requiredProductIds: releaseGroup.requiredProductIds,
        requiredRoleIds: releaseGroup.requiredRoleIds,
        requireRegistration: releaseGroup.requireRegistration,
        requireNotWon: releaseGroup.requireNotWon,
        visibility: releaseGroup.visibility,
        meta: releaseGroup.meta,
        showRoomAvailability: releaseGroup.showRoomAvailability,
        forceEntry: releaseGroup.forceEntry,
        status: releaseGroup.status,
      };

      await api.updateHotelReleaseById(inputGroup.id, payload);

      toast.success(ts('successfully_updated_release_group'));

      history.push('/housekeeping/hotel/groups');

      return;
    }

    const payload: HotelReleaseGroupCreateAttributes = {
      name: releaseGroup.name,
      displayName: releaseGroup.displayName,
      description: releaseGroup.description,
      entryStartAt: releaseGroup.entryStartAt,
      entryEndAt: releaseGroup.entryEndAt,
      type: releaseGroup.type,
      status: releaseGroup.status,
      releaseStartAt: releaseGroup.releaseStartAt,
      releaseEndAt: releaseGroup.releaseEndAt,
      validityPeriod: releaseGroup.validityPeriod,
      requireRegistration: ReleaseRequireRegistration.Required,
      requireNotWon: releaseGroup.requireNotWon,
      showRoomAvailability: releaseGroup.showRoomAvailability,
      visibility: releaseGroup.visibility,
      integrationId: releaseGroup.integrationId,
      requiredProductIds: releaseGroup.requiredProductIds,
      requiredRoleIds: releaseGroup.requiredRoleIds,
      forceEntry: releaseGroup.forceEntry,
      meta: releaseGroup.meta,
    };

    await api.createHotelReleaseGroup(payload);

    toast.success(ts('successfully_created_release_group'));

    // route back to list
    history.push('/housekeeping/hotel/groups');
  }, [inputGroup, releaseGroup, ts, history]);

  return (
    <Form onSubmit={form.onSubmit}>
      <Row id="createReleaseGroupForm">
        <Col md={12}>
          <LinkButton color="secondary" to="/housekeeping/hotel/groups">
            {ts('back')}
          </LinkButton>
        </Col>
        <Col md={12}>
          <h2>{inputGroup ? 'Update Release Group' : 'Create Release Group'}</h2>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label for="name">{ts('release_group_name')}</Label>
            <Input
              id="name"
              onChange={setName}
              readOnly={form.saving}
              required
              value={releaseGroup.name}
            />
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label for="displayName">{ts('common.display_name')}</Label>
            <Input
              id="displayName"
              onChange={setDisplayName}
              readOnly={form.saving}
              value={releaseGroup.displayName}
            />
          </FormGroup>
        </Col>
        <Col md={12}>
          <FormGroup>
            <Label for="description">{ts('description')}</Label>
            <Input
              id="description"
              onChange={setDescription}
              readOnly={form.saving}
              type="textarea"
              value={releaseGroup.description}
            />
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label for="type">{ts('fee.status')}</Label>
            <Input
              defaultValue={releaseGroup.status}
              id="status"
              name="status"
              onChange={setStatus}
              type="select"
            >
              {releaseGroupStatus.map((pt) => (
                <option key={pt.key} value={pt.key}>
                  {pt.name}
                </option>
              ))}
            </Input>
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label for="type">{ts('type')}</Label>
            <Input
              defaultValue={releaseGroup.type}
              disabled={form.saving || !!inputGroup}
              id="type"
              name="type"
              onChange={setType}
              type="select"
            >
              {releaseGroupTypes.map((pt) => (
                <option key={pt.key} value={pt.key}>
                  {pt.name}
                </option>
              ))}
            </Input>
          </FormGroup>
        </Col>

        {releaseGroup.type === ReleaseGroupType.RandomDraw && (
          // Double check this should only be showing up for raffles
          <>
            <Col md={6}>
              <FormGroup>
                <Label for="entryStartAt">{ts('entry_start_at')}</Label>
                <NullableTimeZoneInputField
                  defaultValue={releaseGroup.entryStartAt}
                  disabled={form.saving}
                  id="entryStartAt"
                  onChange={setEntryStartAt}
                  required
                  timeZone={timeZone}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="entryEndAt">{ts('entry_end_at')}</Label>
                <NullableTimeZoneInputField
                  defaultValue={releaseGroup.entryEndAt}
                  disabled={form.saving}
                  id="entryEndAt"
                  onChange={setEntryEndAt}
                  required
                  timeZone={timeZone}
                />
              </FormGroup>
            </Col>
          </>
        )}
        <Col md={6}>
          <FormGroup>
            <Label for="releaseStartAt">{ts('release_start_at')}</Label>
            <TimeZoneInputField
              defaultValue={releaseGroup.releaseStartAt}
              disabled={form.saving}
              id="releaseStartAt"
              onChange={setReleaseStartAt}
              required
              timeZone={timeZone}
            />
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label for="releaseEndAt">{ts('release_end_at')}</Label>
            <TimeZoneInputField
              defaultValue={releaseGroup.releaseEndAt}
              disabled={form.saving}
              id="releaseEndAt"
              onChange={setReleaseEndAt}
              required
              timeZone={timeZone}
            />
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label for="validityPeriod">{ts('validity_period')}</Label>
            <Input
              id="validityPeriod"
              onChange={setValidityPeriod}
              readOnly={form.saving}
              required
              type="number"
              value={releaseGroup.validityPeriod}
            />
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label for="requireRegistration">{ts('require_registration')}</Label>
            <Input
              disabled={form.saving}
              id="requireRegistration"
              name="requireRegistration"
              onChange={setRequireRegistration}
              type="select"
              value={releaseGroup.requireRegistration}
            >
              {requireRegistrationValues.map((pt) => (
                <option key={pt.key} value={pt.key}>
                  {pt.name}
                </option>
              ))}
            </Input>
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label for="forceEntry">{ts('force_entry')}</Label>
            <Input
              disabled={form.saving}
              id="forceEntry"
              name="forceEntry"
              onChange={setForceEntry}
              type="select"
              value={releaseGroup.forceEntry}
            >
              {releaseForceEntryValues.map((pt) => (
                <option key={pt.key} value={pt.key}>
                  {pt.name}
                </option>
              ))}
            </Input>
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup check className="mt-5">
            <Input
              checked={releaseGroup.requireNotWon}
              id="requireNotWon"
              onChange={setRequireNotWon}
              readOnly={form.saving}
              type="checkbox"
            />
            <Label check for="requireNotWon">
              {ts('require_not_won')}
            </Label>
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <FormGroup check>
              <Input
                checked={releaseGroup.showRoomAvailability}
                id="showRoomAvailability"
                onChange={setShowRoomAvailability}
                readOnly={form.saving}
                type="checkbox"
              />
              <Label check for="showRoomAvailability">
                {ts('show_room_availability')}
              </Label>
            </FormGroup>
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label for="visibility">{ts('visibility')}</Label>
            <Input
              id="visibility"
              name="visibility"
              onChange={({ currentTarget: { value } }) => {
                setVisibility(value as ReleaseGroupVisibility);
              }}
              type="select"
              value={releaseGroup.visibility}
            >
              {releaseGroupVisibilities.map((pt) => (
                <option key={pt.key} value={pt.key}>
                  {pt.name}
                </option>
              ))}
            </Input>
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label for="integrationId">{ts('hotel_integration')}</Label>
            <Input
              defaultValue={releaseGroup.integrationId}
              disabled={form.saving || !!inputGroup}
              id="integrationId"
              name="integrationId"
              onChange={setIntegrationId}
              required
              type="select"
            >
              <option disabled key="" value="">
                {ts('select_a_hotel_integration')}
              </option>
              {integrations.data?.map((pt) => (
                <option key={pt.id} value={pt.id}>
                  {pt.name}
                </option>
              ))}
            </Input>
          </FormGroup>
        </Col>
        <Col lg={6} xs={12}>
          <FormGroup>
            <Label for="roleRestriction">{ts('restrict_to_roles')}</Label>
            <RoleSelection
              permissionError={ts('you_do_not_have_sufficient_release_groups_by_role')}
              selectedIds={releaseGroup.requiredRoleIds}
              setSelectedIds={setRoleIds}
            />
          </FormGroup>
        </Col>
        <Col lg={6} xs={12}>
          <FormGroup>
            <Label for="grantRestriction">{ts('restrict_to_products')}</Label>
            <ProductSelection
              excludedIds={[]}
              permissionError="You do not have sufficient permission to limit release groups by product"
              selectedIds={releaseGroup.requiredProductIds}
              setSelectedIds={setProductIds}
            />
          </FormGroup>
        </Col>
        {integration?.type === HotelIntegrationProviders.Meetingmax && (
          <MeetingMaxMetaFields
            meta={releaseGroup.meta ?? {}}
            saving={form.saving}
            setMeta={(meta) => {
              setReleaseGroup({ ...releaseGroup, meta });
            }}
          />
        )}
        {integration?.type === HotelIntegrationProviders.Passkey && (
          <PasskeyMetaFields
            meta={releaseGroup.meta ?? {}}
            saving={form.saving}
            setMeta={(meta) => {
              setReleaseGroup({ ...releaseGroup, meta });
            }}
          />
        )}
        <Col md={12}>
          <Button color="primary" disabled={form.saving} id="submit" type="submit">
            {ts('submit')}
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

interface MetaFieldsProps {
  readonly meta: Record<string, string>;
  readonly saving: boolean;
  readonly setMeta: (meta: Record<string, string>) => void;
}

const MeetingMaxMetaFields: FC<MetaFieldsProps> = ({ meta, setMeta, saving }) => {
  const setGroupCode = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const groupCode = event.currentTarget.value;
      setMeta({ ...meta, groupCode });
    },
    [meta, setMeta],
  );

  const { ts } = useTranslation();

  return (
    <Col lg={6} xs={12}>
      <FormGroup>
        <Label for="groupCode">{ts('meeting_max_group_code')}</Label>
        <Input
          id="groupCode"
          onChange={setGroupCode}
          readOnly={saving}
          value={meta['groupCode'] ?? ''}
        />
        <FormText color="muted">
          {ts(
            'if_this_is_the_main_block_and_it_has_no_associated_group_code_in_meetingmax_leave_this_field_blank',
          )}
        </FormText>
      </FormGroup>
    </Col>
  );
};

const PasskeyMetaFields: FC<MetaFieldsProps> = ({ meta, setMeta, saving }) => {
  const setGroupCode = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const groupCode = event.currentTarget.value;
      setMeta({ ...meta, groupCode });
    },
    [meta, setMeta],
  );

  const { ts } = useTranslation();

  return (
    <Col lg={6} xs={12}>
      <FormGroup>
        <Label for="groupCode">{ts('passkey_attendee_access_code')}</Label>
        <Input
          id="groupCode"
          onChange={setGroupCode}
          readOnly={saving}
          value={meta['groupCode'] ?? ''}
        />
        <FormText color="muted">{ts('passkey_attendee_access_code_description')}</FormText>
      </FormGroup>
    </Col>
  );
};
